import React, { useMemo, useEffect } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { RootState } from "us.helper/types";
import { extendedFieldValidationSchema } from "../../Validations";
import { getExtendedFieldFormValues } from "../Functions";
import * as Actions from "us.collection.admin/actions";
import { DeleteOutlined, PlusOutlined } from "us.icons";
import {
  ExtendedFieldsActions,
  EXTENDED_FIELD_CATEGORIES,
  ExtendedFieldsSourceType,
} from "us.collection.admin/constants";
import { ExtendedFieldDataTypes } from "us.common/constants";
import { ExtendedFieldsDetailsProps } from "us.collection.admin/components/ExtendedFields/Interfaces";
import { SaveExtendedField } from "us.collection.admin/components/ExtendedFields/Repository";
import _ from "lodash";
const {
  $Button,
  $TextArea,
  $Form,
  $Input,
  $Popconfirm,
  $Select,
  $Row,
  $Col,
  $Tooltip,
  $Radio,
} = Common.Components;

/**
 * @description - Extended fields detail component
 * @link Design Document - https://unicorn-solutions.atlassian.net/browse/UBX4-23848
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 18/03/2025
 */
const ExtendedFieldDetail: React.FC<
  ExtendedFieldsDetailsProps & PropsFromRedux
> = (props) => {
  const { t } = useTranslation();
  const {
    dataTypes,
    fields,
    drawerInfo,
    isFieldSaving,
    storedProcedures,
    onCancel,
    newExtendedField,
    updateExtendedField,
    getStoredProcedures,
    getDataTypes,
  } = props;

  const { id: selectedFieldId, type: drawerType } = drawerInfo ?? {};

  useEffect(() => {
    getStoredProcedures && getStoredProcedures({});
    getDataTypes && getDataTypes({});
  }, []);

  const isEdit = drawerType === ExtendedFieldsActions.EDIT;

  const initFieldDetails = useMemo(
    () => getExtendedFieldFormValues(selectedFieldId, fields.data) ?? {},
    [fields.data, selectedFieldId]
  );

  const isDebtorStatus = useMemo(() => {
    return initFieldDetails?.fieldName === "DebtorStatus";
  }, [initFieldDetails]);

  const handleSubmit = (values: any, actions: any) => {
    const saveParams = SaveExtendedField.call({ ...values, dataTypes });
    if (drawerType === ExtendedFieldsActions.NEW) {
      newExtendedField && newExtendedField(saveParams);
    } else if (isEdit) {
      updateExtendedField && updateExtendedField(saveParams);
    }
    actions.setSubmitting(false);
  };

  /**
   * @Description Add new value
   */
  const handleAdd = (setValues: any) => {
    setValues(
      (preValues: any) => ({
        ...preValues,
        valueList: [...preValues?.valueList, undefined],
      }),
      false
    );
  };

  /**
   * @Description Delete value
   */
  const handleDelete = (
    valueList: any[],
    index: number,
    setFieldValue: any
  ) => {
    let newValueList = [...valueList];
    if (newValueList?.length > 1) {
      newValueList?.splice(index, 1);
      setFieldValue("valueList", newValueList);
    }
  };

  return (
    <Formik
      initialValues={initFieldDetails}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
      validationSchema={extendedFieldValidationSchema(fields.data)}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleSubmit,
        isSubmitting,
        isValid,
        setValues,
        setFieldValue,
        validateForm,
        dirty,
      }: any) => (
        <>
          <$Form layout="vertical" className="extended-fields">
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
                <$Select
                  name="fieldCategory"
                  formitem={{
                    label: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.CATEGORY"),
                  }}
                  allOption={false}
                  required
                  options={EXTENDED_FIELD_CATEGORIES}
                  size="small"
                  disabled={isDebtorStatus}
                />
              </$Col>
              <$Col span={12}>
                <$Input
                  label={t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.FIELD_NAME")}
                  name="fieldName"
                  value={values.fieldName}
                  size="small"
                  required
                  maxLength={150}
                  disabled={isEdit || isDebtorStatus}
                />
              </$Col>
            </$Row>
            <$TextArea
              formItem={{
                label: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.DESCRIPTION"),
              }}
              name="description"
              value={values.description}
              className="mb-2"
            />
            <$Row gutter={16} className="mb-3">
              <$Col span={12}>
                <$Select
                  name="dataTypeId"
                  formitem={{
                    label: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.VALUE_TYPE"),
                  }}
                  value={values?.dataTypeId}
                  allOption={false}
                  required
                  options={dataTypes.data}
                  loading={dataTypes.isLoading}
                  disabled={dataTypes.isLoading || isDebtorStatus}
                  optionValue="id"
                  optionText="dataType"
                  size="small"
                  onChange={(value) => {
                    setValues(
                      (preValues: any) => ({
                        ...preValues,
                        dataType: _.find(dataTypes.data, { id: value })
                          ?.dataType,
                        storedProcedure: undefined,
                        valueList: [undefined],
                        valueType: ExtendedFieldsSourceType.SP,
                      }),
                      false
                    );
                    setTimeout(() => {
                      validateForm();
                    }, 0);
                  }}
                />
              </$Col>
            </$Row>
            {values?.dataType?.trim()?.toLowerCase() ==
              ExtendedFieldDataTypes.CUSTOM && (
              <>
                <label className="ant-form-item-required mt-2">
                  {t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.VALUE_SOURCE")}
                </label>
                <$Radio
                  className="dl-radio mt-2 w-100"
                  formitem
                  optionContent
                  name="valueType"
                  optionText="label"
                  optionValue="value"
                  defaultValue={ExtendedFieldsSourceType.SP}
                  size="small"
                  options={[
                    {
                      value: ExtendedFieldsSourceType.SP,
                      label: (
                        <>
                          <small>
                            <strong className="mb-2 ml-3">
                              {t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.FROM_SP")}
                            </strong>
                          </small>
                          <div
                            className="d-flex flex-fill dl-radio-option"
                            style={{
                              opacity:
                                values?.valueType != ExtendedFieldsSourceType.SP
                                  ? 0.5
                                  : 1,
                            }}
                          >
                            <$Select
                              name="storedProcedure"
                              formitem={{}}
                              allOption={false}
                              required
                              placeholder={t(
                                "US.COLLECTION.ADMIN:EXTENDED_FIELDS.SELECT_STORED_PROCEDURE"
                              )}
                              options={storedProcedures.data}
                              loading={storedProcedures.isLoading}
                              disabled={storedProcedures.isLoading || isDebtorStatus}
                              size="small"
                              style={{ width: "100%" }}
                              onChange={() => {
                                setTimeout(() => {
                                  validateForm();
                                }, 0);
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                setFieldValue(
                                  "valueType",
                                  ExtendedFieldsSourceType.SP
                                );
                              }}
                            />
                          </div>
                        </>
                      ),
                      disabled: isDebtorStatus,
                    },
                    {
                      value: ExtendedFieldsSourceType.VALUE,
                      label: (
                        <>
                          <small>
                            <strong className="mb-3 ml-3">
                              {t(
                                "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FROM_VALUES"
                              )}
                            </strong>
                          </small>
                          {values?.valueList?.map(
                            (valueRecord: any, index: number) => (
                              <div
                                className="info-item-container"
                                key={index}
                                style={{
                                  opacity:
                                    values?.valueType !=
                                    ExtendedFieldsSourceType.VALUE
                                      ? 0.5
                                      : 1,
                                }}
                              >
                                <div className="info-content-items">
                                  <$Row
                                    gutter={20}
                                    className="align-content-center w-100"
                                  >
                                    <$Col span={24}>
                                      <$Input
                                        name={`valueList[${index}]`}
                                        size="small"
                                        required
                                        maxLength={150}
                                        placeholder={t(
                                          "US.COLLECTION.ADMIN:EXTENDED_FIELDS.ENTER_A_VALUE"
                                        )}
                                        onClick={() => {
                                          setFieldValue(
                                            "valueType",
                                            ExtendedFieldsSourceType.VALUE
                                          );
                                        }}
                                      />
                                    </$Col>
                                  </$Row>
                                </div>
                                {true && (
                                  <div
                                    className="d-flex align-items-center justify-content-end"
                                    style={{ width: "90px" }}
                                  >
                                    <div className="d-flex h-100 align-items-center">
                                      <$Popconfirm
                                        title={t("COMMON.SURE_TO_DELETE_?")}
                                        placement="topLeft"
                                        onConfirm={() => {
                                          handleDelete(
                                            values?.valueList,
                                            index,
                                            setFieldValue
                                          );
                                        }}
                                      >
                                        <$Tooltip
                                          placement="topLeft"
                                          title={t(
                                            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.DELETE_VALUE"
                                          )}
                                        >
                                          <div
                                            className="info-delete-btn"
                                            hidden={
                                              values?.valueList?.length == 1
                                            }
                                          >
                                            <DeleteOutlined   
                                              className={"text-error"}
                                            />
                                          </div>
                                        </$Tooltip>
                                      </$Popconfirm>
                                      {values?.valueList?.length - 1 ==
                                        index && (
                                        <$Tooltip
                                          placement="topLeft"
                                          title={t(
                                            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.ADD_NEW_VALUE"
                                          )}
                                        >
                                          <div
                                            className="add-btn"
                                            onClick={() => {
                                              handleAdd(setValues);
                                            }}
                                          >
                                            <PlusOutlined className="add-btn-tex"  />
                                          </div>
                                        </$Tooltip>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </$Form>
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Popconfirm
                title={t("COMMON.SURE_TO_PROCEED_?")}
                placement="topLeft"
                data-testid="pop-confirm"
                okButtonProps={{
                  dataTestid: "pop-confirm-ok",
                }}
                onConfirm={() => handleSubmit()}
                okText={t("COMMON.YES")}
                cancelText={t("COMMON.NO")}
                disabled={!isValid || isSubmitting || isFieldSaving || !dirty}
              >
                <$Button
                  className="ml-3 mr-2"
                  type="primary"
                  data-testid="save-btn"
                  loading={isSubmitting || isFieldSaving}
                  disabled={!isValid || isSubmitting || isFieldSaving || !dirty}
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
              </$Popconfirm>
              <$Button
                data-testid="cancel-item-type"
                onClick={() => onCancel()}
              >
                {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { extendedField } = state;
  const { fields, dataTypes, drawerInfo, isFieldSaving, storedProcedures } =
    extendedField;
  return { fields, drawerInfo, isFieldSaving, storedProcedures, dataTypes };
};

const { storedProcedures, newExtendedField, extendedFieldDetails, dataTypes } =
  Actions.extendedFields;

const mapDispatchToProps = {
  newExtendedField: newExtendedField.save,
  updateExtendedField: extendedFieldDetails.save,
  getStoredProcedures: storedProcedures.get,
  getDataTypes: dataTypes.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ExtendedFieldDetail);
